import { NoteFlagChip, type NoteFlag } from '@app/features/visits/noteFlag';
import { useAuthContext } from '@context/auth/useAuthContext';
import { Button, Checkbox, Input } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AUDIENCE_OPTIONS,
  buildAudience,
  buildDoctorAudience,
  kindForAudience,
  NOTE_ROLE_LABEL_KEYS,
  OFFICE_ROLE,
  type AudienceGroup,
} from './noteAudience';
import { CreateNotePayload, NoteFlagValue } from './notes.types';
import { useCreateNote } from './useNotes';

type NoteComposerProps = {
  clientId: number;
  /** Set to anchor the note to a visit; omitted, it belongs to the client. */
  visitId?: number | null;
  onWritten?: () => void;
};

/**
 * Writing a note.
 *
 * The audience is a first-class choice, not a default: `notes.audience` is what the
 * row-level policy reads, so ticking "to the doctor" is the difference between a
 * doctor seeing a remark and not. It is asked as recipient groups rather than as a
 * single choice because one note often goes to more than one of them.
 *
 * The office is always a recipient and is shown as such: the note is the office's own,
 * and an audience without the author's role is a note the author cannot read back.
 * Ticking nothing is not an error — it is the office-only note, and the hint says so.
 *
 * Validation is per field. The button stays live and says what is wrong on the field
 * that is wrong; a button that quietly will not press tells the writer nothing.
 */
const NoteComposer: React.FC<NoteComposerProps> = ({ clientId, visitId = null, onWritten }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();

  // The choice belongs to whoever can make it; everyone else is TOLD who will read
  // their note rather than asked.
  const canChooseAudience = !!user?.isAdmin || !!user?.isOfficeAdmin;

  // A doctor writes to the office and to nobody else. Doctors and caregivers do not
  // write to each other, so the default the rest of the app uses - the care team -
  // would be the one audience a doctor must never address. Office-only is not a
  // narrowing of their choice; it is the whole of it.
  const isDoctor = !!user?.isDoctor && !canChooseAudience;

  const [groups, setGroups] = useState<AudienceGroup[]>(canChooseAudience ? [] : isDoctor ? [] : ['cgv']);
  const [body, setBody] = useState('');
  const [flag, setFlag] = useState<NoteFlag | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);

  const { createNote, isCreatingNote, createNoteError } = useCreateNote(clientId);

  const chosenOptions = AUDIENCE_OPTIONS.filter((option) => groups.includes(option.role));

  const submit = async () => {
    const trimmed = body.trim();

    if (trimmed.length === 0) {
      setBodyError(t('notes-validation-body-required'));
      return;
    }
    setBodyError(null);

    const audience = isDoctor ? buildDoctorAudience() : buildAudience(groups);
    const payload: CreateNotePayload = {
      visitId,
      kind: kindForAudience(audience),
      audience,
      body: trimmed,
      flag: (flag as NoteFlagValue | null) ?? null,
    };

    try {
      await createNote(payload);
      setBody('');
      setFlag(null);
      if (canChooseAudience) setGroups([]);
      onWritten?.();
    } catch {
      // The failure is rendered below from the mutation's own error; nothing the
      // writer typed is discarded, so they can press again once it is back.
    }
  };

  return (
    <div className="border-0 border-t border-slate-100 p-3">
      {/* A form in its own box, under the thread rather than attached to it: writing a
          note is an act of its own, not the reply line of a conversation. */}
      <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3.5">
        <div className="text-[13px] font-semibold text-slate-700">{t('notes-composer-title')}</div>

        <div>
          <Input.TextArea
            value={body}
            onChange={(event) => {
              setBody(event.target.value);
              if (bodyError) setBodyError(null);
            }}
            status={bodyError ? 'error' : undefined}
            aria-label={t('notes-composer-body-label')}
            placeholder={t('notes-composer-body-placeholder')}
            autoSize={{ minRows: 3, maxRows: 10 }}
            maxLength={4000}
          />
          {bodyError && (
            <div role="alert" className="mt-1 text-[12px] text-[#ff4d4f]">
              {bodyError}
            </div>
          )}
        </div>

        <div>
          {/* A WRITER WHO CANNOT CHOOSE IS TOLD, NOT ASKED. "Who should see this?"
              over a single fixed value, with a sentence underneath explaining the
              same fact a third time, is three lines for something the writer
              cannot change. One statement of where the note goes is the whole of
              it. The office picks recipients, so the office still gets the
              question and the per-choice hints. */}
          {canChooseAudience ? (
            <>
              <div className="mb-1 text-[12px] font-medium text-slate-600">{t('notes-composer-audience-label')}</div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <Checkbox checked disabled>
                  {t(NOTE_ROLE_LABEL_KEYS[OFFICE_ROLE])}
                </Checkbox>
                <Checkbox.Group
                  value={groups}
                  options={AUDIENCE_OPTIONS.map((option) => ({ label: t(option.labelKey), value: option.role }))}
                  onChange={(value) => setGroups(value as AudienceGroup[])}
                />
              </div>
              {/* Who will read it, in words, for whatever is ticked — including nothing. */}
              <div className="mt-1 text-[11px] text-slate-400">
                {chosenOptions.length === 0
                  ? t('notes-audience-internal-hint')
                  : chosenOptions.map((option) => <div key={option.role}>{t(option.hintKey)}</div>)}
              </div>
            </>
          ) : (
            <div className="text-[12px] text-slate-500">
              {t(isDoctor ? 'notes-composer-visible-to-office' : 'notes-composer-visible-to-care-team')}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-0 border-t border-slate-100 pt-3">
          <NoteFlagChip value={flag} onChange={setFlag} />
          <Button type="primary" loading={isCreatingNote} onClick={submit}>
            {t('notes-composer-submit')}
          </Button>
        </div>

        {createNoteError ? (
          <div role="alert" className="text-[12px] text-[#ff4d4f]">
            {t('notes-composer-error')}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default NoteComposer;
